import { useEffect, useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Save } from "lucide-react";
import { toast } from "sonner";
import { adminGetSections, adminPutSections } from "@/lib/api";
import { useSite } from "@/lib/site";

function Row({ id }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`section-row-${id}`}
      className={`flex items-center gap-3 bg-white border border-[#1F3864]/10 rounded-xl px-4 py-3.5 ${isDragging ? "shadow-lg z-10 relative" : ""}`}>
      <button {...attributes} {...listeners} data-testid={`drag-section-${id}`} className="cursor-grab text-[#1F3864]/40 hover:text-[#1F3864] touch-none">
        <GripVertical size={18} />
      </button>
      <span className="font-bold text-[#1F3864] capitalize">{id}</span>
    </div>
  );
}

export default function SectionsAdmin() {
  const { refresh } = useSite();
  const [order, setOrder] = useState([]);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { adminGetSections().then(setOrder).catch(() => toast.error("Failed to load sections")); }, []);

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrder((o) => arrayMove(o, o.indexOf(active.id), o.indexOf(over.id)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminPutSections(order);
      await refresh();
      toast.success("Section order saved — live on the site");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-sections">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Page Sections</h1>
          <p className="text-[#1F3864]/60">Drag to reorder how sections appear on the landing page.</p>
        </div>
        <button data-testid="save-sections-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold px-5 py-3 rounded-full hover:bg-[#16294B] disabled:opacity-50">
          <Save size={16} /> {saving ? "Saving…" : "Save order"}
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 max-w-lg">
            {order.map((id) => <Row key={id} id={id} />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
